import { useEffect, useState, useMemo } from "react";
import { api } from "../../services/api";
import toast from "react-hot-toast";
import { FaDeleteLeft, FaEye } from "react-icons/fa6";

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// ─── Design tokens ────────────────────────────────────────────────────────────
const T = {
  fontDisplay: "'DM Sans', sans-serif",
  fontBody: "'DM Sans', sans-serif",
  fontMono: "'DM Sans', sans-serif",
  amber: "#111111",
  amberDark: "#333333",
  amberBg: "rgba(0,0,0,0.06)",
  amberBorder: "rgba(0,0,0,0.12)",
  dark: "#0c1118",
  bg: "#f4f6f8",
  surface: "#ffffff",
  border: "#e8ecf0",
  muted: "#6b7280",
  mutedLight: "#9ca3af",
  green: "#16a34a",
  greenBg: "rgba(34,197,94,0.1)",
  greenBorder: "rgba(34,197,94,0.25)",
  blue: "#1d4ed8",
  blueBg: "rgba(59,130,246,0.1)",
  blueBorder: "rgba(59,130,246,0.25)",
  red: "#dc2626",
  redBg: "rgba(239,68,68,0.1)",
  redBorder: "rgba(239,68,68,0.25)",
};

// ─── Inject global CSS once ───────────────────────────────────────────────────
if (!document.getElementById("apla-styles")) {
  const s = document.createElement("style");
  s.id = "apla-styles";
  s.textContent = `
    @keyframes apla-spin { to { transform: rotate(360deg); } }
    @keyframes apla-fadeup {
      from { opacity: 0; transform: translateY(10px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    .apla-spin { animation: apla-spin 0.8s linear infinite; }
    .apla-fadein { animation: apla-fadeup 0.25s ease both; }
    .apla-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 24px rgba(0,0,0,0.10); }
    .apla-kpi { transition: transform 0.2s, box-shadow 0.2s; }
    .apla-row:hover td { background: rgba(0,0,0,0.03); }
    .apla-tab-btn:hover { color: #0c1118; }
    .apla-btn:hover { opacity: 0.82; }
    .apla-icon-btn:hover { background: rgba(0,0,0,0.08); }
    .apla-org-row:hover td { background: rgba(0,0,0,0.03); }
    .apla-input:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
    .apla-select:focus { outline: none; border-color: #111111; box-shadow: 0 0 0 2px rgba(0,0,0,0.12); }
    .apla-close-btn:hover { background: rgba(0,0,0,0.08); }
    .apla-toggle-btn:hover { background: rgba(0,0,0,0.06); }

    /* Filter drawer — class-based transitions (more reliable than inline-style transitions in React) */
    .apla-drawer-wrap {
      position: fixed; top: 0; left: 0; right: 0; bottom: 0;
      z-index: 9999; display: flex; justify-content: flex-end;
      pointer-events: none;
    }
    .apla-drawer-wrap.is-open { pointer-events: auto; }

    .apla-backdrop {
      position: absolute; top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0,0,0,0.45);
      opacity: 0; pointer-events: none;
      transition: opacity 0.28s ease;
    }
    .apla-drawer-wrap.is-open .apla-backdrop {
      opacity: 1; pointer-events: auto;
    }

    .apla-drawer-panel {
      position: relative; height: 100%; width: 100%; max-width: 420px;
      background: #ffffff; border-left: 1px solid #e8ecf0;
      box-shadow: -8px 0 40px rgba(0,0,0,0.16);
      display: flex; flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
    }
    .apla-drawer-wrap.is-open .apla-drawer-panel {
      transform: translateX(0);
    }

    /* PDF viewer modal — same pattern */
    .apla-modal-wrap {
      position: fixed; inset: 0; z-index: 9999;
      background: rgba(0,0,0,0.65);
      display: flex; align-items: center; justify-content: center;
    }
  `;
  document.head.appendChild(s);
}

// ─── Status helpers ───────────────────────────────────────────────────────────
function statusBadgeStyle(status) {
  switch (status) {
    case "PENDING_PAYMENT":
    case "PENDING":
      return { background: T.amberBg, color: T.amberDark, border: `1px solid ${T.amberBorder}` };
    case "PROCESSING":
      return { background: T.blueBg, color: T.blue, border: `1px solid ${T.blueBorder}` };
    case "COMPLETED":
      return { background: T.greenBg, color: T.green, border: `1px solid ${T.greenBorder}` };
    case "CANCELED":
    case "CANCELLED":
      return { background: T.redBg, color: T.red, border: `1px solid ${T.redBorder}` };
    default:
      return { background: "rgba(107,114,128,0.1)", color: T.muted, border: "1px solid rgba(107,114,128,0.2)" };
  }
}

function rowBorderColor(status) {
  switch (status) {
    case "PENDING_PAYMENT":
    case "PENDING":
      return T.amber;
    case "PROCESSING":
      return "#3b82f6";
    case "COMPLETED":
      return "#22c55e";
    case "CANCELED":
    case "CANCELLED":
      return "#ef4444";
    default:
      return T.border;
  }
}

function companyApprovalStyle(s) {
  if (s === "approved") return { background: T.greenBg, color: T.green };
  if (s === "rejected") return { background: T.redBg, color: T.red };
  return { background: T.amberBg, color: T.amberDark };
}

// ─── Shared sub-components ────────────────────────────────────────────────────
function FilterLabel({ children }) {
  return (
    <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.muted, letterSpacing: "0.06em", marginBottom: 4, fontFamily: T.fontBody }}>
      {children}
    </label>
  );
}

function FInput({ value, onChange, placeholder, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className="apla-input"
      style={{ width: "100%", padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark, boxSizing: "border-box" }}
    />
  );
}

function FSelect({ value, onChange, children }) {
  return (
    <select
      value={value}
      onChange={onChange}
      className="apla-select"
      style={{ width: "100%", padding: "8px 12px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark, boxSizing: "border-box" }}
    >
      {children}
    </select>
  );
}

// ─── Icons (inline SVG paths) ─────────────────────────────────────────────────
const IconDoc = () => (
  <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);
const IconClock = () => (
  <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconRefresh = () => (
  <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);
const IconCheck = () => (
  <svg width={20} height={20} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);
const IconFilter = () => (
  <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h18M6 8h12M9 12h6M11 16h2" />
  </svg>
);
const IconX = () => (
  <svg width={16} height={16} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);
const IconUpload = () => (
  <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);
const IconExternal = () => (
  <svg width={14} height={14} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

// ─── Mobile plan request card ─────────────────────────────────────────────────
function PlanMobileCard({ r, uploadingId, onUpload, onView, onRemove, onOpenEditor, onToggleAccess, onUpdateStatus }) {
  const isOrg = r.source === "org";
  const isIndividual = r.source === "individual";
  const badgeStyle = statusBadgeStyle(r.status);
  const borderColor = rowBorderColor(r.status);
  const typeLabel = r.type === "REWORK" ? `Rework #${(r.reworkRound ?? 0) || 1}` : "New request";
  const isUnlocked = r.requesterAccessLock === "unlocked";
  const hasFile = !!r.fileUrl;
  const canUpload = ["PROCESSING", "COMPLETED"].includes(r.status) && !hasFile;

  function formatStatusLabel(status) {
    if (!status) return "-";
    return status.replace(/_/g, " ").toLowerCase().replace(/^\w/, (c) => c.toUpperCase());
  }

  return (
    <div style={{
      background: T.surface, borderRadius: 10,
      border: `1px solid ${T.border}`, borderLeft: `4px solid ${borderColor}`,
      marginBottom: 10, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      {/* Top row: title + status */}
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: T.dark, fontSize: 14, marginBottom: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {r.projectTitle}
          </div>
          <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontBody }}>{typeLabel}</div>
          <div style={{ fontSize: 12, color: T.muted, marginTop: 4 }}>{r.requesterName}</div>
          <div style={{ fontSize: 11, color: T.mutedLight }}>{r.requesterEmail}</div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6, flexShrink: 0 }}>
          <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, whiteSpace: "nowrap", ...badgeStyle }}>
            {formatStatusLabel(r.status)}
          </span>
          {isOrg ? (
            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: T.blueBg, color: T.blue, fontFamily: T.fontBody }}>
              Company
            </span>
          ) : (
            <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 10, fontWeight: 700, background: "rgba(107,114,128,0.1)", color: T.muted, fontFamily: T.fontBody }}>
              Individual
            </span>
          )}
        </div>
      </div>

      {/* Action strip */}
      <div style={{ padding: "10px 14px", borderTop: `1px solid ${T.border}`, background: T.bg, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {/* View PDF */}
        {hasFile && (
          <button
            type="button"
            className="apla-btn"
            onClick={onView}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", fontSize: 12, fontWeight: 600, background: T.dark, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            <FaEye /> View PDF
          </button>
        )}
        {/* Delete PDF */}
        {hasFile && (
          <button
            type="button"
            className="apla-btn"
            onClick={onRemove}
            style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 12px", fontSize: 12, fontWeight: 600, background: T.redBg, color: T.red, border: `1px solid ${T.redBorder}`, borderRadius: 6, cursor: "pointer" }}
          >
            <FaDeleteLeft /> Delete
          </button>
        )}
        {/* Upload PDF */}
        {canUpload && (
          <label style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "6px 12px", fontSize: 12, fontWeight: 600,
            background: uploadingId === r.id ? T.greenBg : "#16a34a",
            color: uploadingId === r.id ? T.green : "#fff",
            border: uploadingId === r.id ? `1px solid ${T.greenBorder}` : "none",
            borderRadius: 6, cursor: uploadingId === r.id ? "not-allowed" : "pointer",
          }}>
            {uploadingId === r.id ? (
              <>
                <div className="apla-spin" style={{ width: 11, height: 11, border: "2px solid #16a34a", borderTopColor: "#fff", borderRadius: "50%" }} />
                Uploading…
              </>
            ) : (
              <><IconUpload /> Upload PDF</>
            )}
            <input type="file" accept="application/pdf" style={{ display: "none" }} disabled={uploadingId === r.id} onChange={(e) => onUpload(e.target.files?.[0], e.target)} />
          </label>
        )}
        {/* Open editor */}
        {r.editorUrl && (
          <button
            type="button"
            className="apla-btn"
            onClick={onOpenEditor}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 12px", fontSize: 12, fontWeight: 600, background: T.dark, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
          >
            <IconExternal /> Editor
          </button>
        )}
        {/* Individual access toggle */}
        {isIndividual && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
            <span style={{ fontSize: 11, color: T.muted }}>Access</span>
            <div
              onClick={onToggleAccess}
              style={{ position: "relative", width: 36, height: 20, borderRadius: 99, background: isUnlocked ? "#22c55e" : T.border, display: "flex", alignItems: "center", padding: "0 2px", cursor: "pointer", transition: "background 0.2s" }}
            >
              <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transform: isUnlocked ? "translateX(16px)" : "translateX(0)", transition: "transform 0.2s" }} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Mobile org/company card ──────────────────────────────────────────────────
function OrgMobileCard({ o, updatingOrgId, onToggle }) {
  return (
    <div style={{
      background: T.surface, borderRadius: 10,
      border: `1px solid ${T.border}`,
      marginBottom: 8, overflow: "hidden",
      boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    }}>
      <div style={{ padding: "12px 14px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
        <div>
          <div style={{ fontWeight: 700, color: T.dark, fontSize: 14 }}>{o.orgName}</div>
          <div style={{ fontSize: 11, color: T.muted, marginTop: 3 }}>
            {o.stats?.total ?? 0} total • {o.stats?.completed ?? 0} completed
          </div>
          <div style={{ fontSize: 11, color: T.mutedLight, marginTop: 2 }}>
            {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "-"}
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
          <span style={{
            display: "inline-block", padding: "3px 10px", borderRadius: 99,
            fontSize: 11, fontWeight: 700, fontFamily: T.fontBody,
            background: o.accessLock === "locked" ? T.redBg : T.greenBg,
            color: o.accessLock === "locked" ? T.red : T.green,
          }}>
            {o.accessLock === "locked" ? "Locked" : "Unlocked"}
          </span>
          <button
            type="button"
            className="apla-toggle-btn"
            onClick={onToggle}
            disabled={updatingOrgId === o.orgId}
            style={{ display: "inline-flex", alignItems: "center", padding: "5px 14px", fontSize: 12, fontWeight: 600, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, cursor: updatingOrgId === o.orgId ? "not-allowed" : "pointer", color: T.dark, opacity: updatingOrgId === o.orgId ? 0.5 : 1 }}
          >
            {updatingOrgId === o.orgId ? "Updating…" : o.accessLock === "locked" ? "Unlock" : "Lock"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Page component ───────────────────────────────────────────────────────────
export default function PurchasePlanAdminPage() {
  const isMobile = useIsMobile();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadingId, setUploadingId] = useState(null);

  const [orgRows, setOrgRows] = useState([]);
  const [loadingOrgAccess, setLoadingOrgAccess] = useState(true);
  const [updatingOrgId, setUpdatingOrgId] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [titleFilter, setTitleFilter] = useState("");
  const [requesterFilter, setRequesterFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Tabs: "requests" | "companies"
  const [activeTab, setActiveTab] = useState("requests");

  // Company search
  const [orgSearch, setOrgSearch] = useState("");

  // Inline PDF viewer
  const [viewer, setViewer] = useState(null);

  const backendBase = process.env.REACT_APP_BACKEND_URL;

  // ---------- Helpers ----------
  function formatStatusLabel(status) {
    if (!status) return "-";
    return status
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/^\w/, (c) => c.toUpperCase());
  }

  // ---------- Data loading ----------
  async function load() {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
      if (sourceFilter && sourceFilter !== "all") params.set("source", sourceFilter);
      if (titleFilter.trim()) params.set("q", titleFilter.trim());
      if (requesterFilter.trim()) params.set("requester", requesterFilter.trim());
      if (dateFrom) params.set("from", dateFrom);
      if (dateTo) params.set("to", dateTo);

      const query = params.toString();
      const url = `${backendBase}/api/purchase-plans${query ? `?${query}` : ""}`;
      const r = await api.get(url);
      setRows(Array.isArray(r) ? r : []);
      setPage(1);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load engineering plan requests");
    } finally {
      setLoading(false);
    }
  }

  async function loadOrgAccess() {
    setLoadingOrgAccess(true);
    try {
      const r = await api.get(`${backendBase}/api/purchase-plans/org-access`);
      setOrgRows(Array.isArray(r) ? r : []);
    } catch (e) {
      console.error(e);
      toast.error("Failed to load company access list");
    } finally {
      setLoadingOrgAccess(false);
    }
  }

  useEffect(() => {
    load();
    loadOrgAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep page in range when data or pageSize changes
  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
    if (page > pageCount) setPage(pageCount);
  }, [rows.length, pageSize, page]);

  // ---------- Company access toggle ----------
  async function toggleOrgAccess(org) {
    const nextLock = org.accessLock === "locked" ? "unlocked" : "locked";
    setUpdatingOrgId(org.orgId);
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/org-access/${org.orgId}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ accessLock: nextLock }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update company access");
      }
      toast.success(nextLock === "locked" ? "Company access locked" : "Company access unlocked");
      loadOrgAccess();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to update company access");
    } finally {
      setUpdatingOrgId(null);
    }
  }

  // ---------- Upload / re-upload plan PDF ----------
  async function complete(id, file, inputElement) {
    if (!file) return;
    if (file.type !== "application/pdf") {
      toast.error("Please upload a PDF file");
      if (inputElement) inputElement.value = "";
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File size must be less than 10MB");
      if (inputElement) inputElement.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploadingId(id);
    try {
      const response = await fetch(`${backendBase}/api/purchase-plans/${id}/complete`, {
        method: "POST",
        body: fd,
        credentials: "include",
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Upload failed with status ${response.status}`);
      }
      toast.success("Plan PDF uploaded successfully");
      if (inputElement) inputElement.value = "";
      load();
    } catch (e) {
      console.error("Upload error:", e);
      toast.error(e?.message || "Upload failed");
      if (inputElement) inputElement.value = "";
    } finally {
      setUploadingId(null);
    }
  }

  async function removeFile(id) {
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/${id}/file`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) throw new Error("Failed to delete file");
      toast.success("Plan PDF deleted");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Delete failed");
    }
  }

  // ---------- Access / approval management ----------
  async function updateAccess(id, payload) {
    try {
      const res = await fetch(`${backendBase}/api/purchase-plans/${id}/access`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Failed to update access");
      }
      toast.success("Access updated");
      load();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Failed to update access");
    }
  }

  // ---------- KPIs & charts ----------
  const kpis = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => (r.status || "").startsWith("PENDING")).length;
    const processing = rows.filter((r) => r.status === "PROCESSING").length;
    const completed = rows.filter((r) => r.status === "COMPLETED").length;
    const bySource = rows.reduce(
      (acc, r) => {
        if (r.source === "org") acc.org += 1;
        else acc.individual += 1;
        return acc;
      },
      { org: 0, individual: 0 }
    );
    return { total, pending, processing, completed, bySource };
  }, [rows]);

  const statusChartData = useMemo(() => {
    const labels = ["Pending", "Processing", "Completed", "Canceled"];
    const statusKeys = ["PENDING_PAYMENT", "PROCESSING", "COMPLETED", "CANCELED"];
    const counts = statusKeys.map((key) =>
      rows.filter((r) => {
        if (key === "PENDING_PAYMENT") return (r.status || "").startsWith("PENDING");
        if (key === "CANCELED") return r.status === "CANCELED" || r.status === "CANCELLED";
        return r.status === key;
      }).length
    );
    const max = Math.max(...counts, 1);
    return labels.map((label, idx) => ({
      label,
      value: counts[idx],
      heightPct: (counts[idx] / max) * 100,
    }));
  }, [rows]);

  const companyStatusChartData = useMemo(() => {
    const orgOnly = rows.filter((r) => r.source === "org");
    const labels = ["Pending", "Processing", "Completed", "Canceled"];
    const statusKeys = ["PENDING_PAYMENT", "PROCESSING", "COMPLETED", "CANCELED"];
    const counts = statusKeys.map((key) =>
      orgOnly.filter((r) => {
        if (key === "PENDING_PAYMENT") return (r.status || "").startsWith("PENDING");
        if (key === "CANCELED") return r.status === "CANCELED" || r.status === "CANCELLED";
        return r.status === key;
      }).length
    );
    const max = Math.max(...counts, 1);
    return labels.map((label, idx) => ({
      label,
      value: counts[idx],
      heightPct: (counts[idx] / max) * 100,
    }));
  }, [rows]);

  // Searchable company list & top 5 companies
  const filteredOrgRows = useMemo(() => {
    const term = orgSearch.trim().toLowerCase();
    if (!term) return orgRows;
    return orgRows.filter((o) => (o.orgName || "").toLowerCase().includes(term));
  }, [orgRows, orgSearch]);

  const topOrgByRequests = useMemo(() => {
    return [...orgRows]
      .filter((o) => (o.stats?.total || 0) > 0)
      .sort((a, b) => (b.stats?.total || 0) - (a.stats?.total || 0))
      .slice(0, 5);
  }, [orgRows]);

  // Pagination calculations
  const pageCount = Math.max(1, Math.ceil(rows.length / pageSize));
  const startIndex = (page - 1) * pageSize;
  const endIndex = Math.min(rows.length, startIndex + pageSize);
  const paginatedRows = useMemo(() => rows.slice(startIndex, endIndex), [rows, startIndex, endIndex]);

  // KPI card definitions
  const kpiCards = [
    {
      label: "Total requests",
      value: kpis.total,
      subtitle: `${kpis.bySource.org} company • ${kpis.bySource.individual} individual`,
      accent: T.amber,
      Icon: IconDoc,
    },
    {
      label: "Pending",
      value: kpis.pending,
      subtitle: "Waiting for payment / action",
      accent: "#111111",
      Icon: IconClock,
    },
    {
      label: "Processing",
      value: kpis.processing,
      subtitle: "Under review by Ceed Civil",
      accent: "#3b82f6",
      Icon: IconRefresh,
    },
    {
      label: "Completed",
      value: kpis.completed,
      subtitle: "Plan uploaded",
      accent: "#22c55e",
      Icon: IconCheck,
    },
  ];

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: T.bg, fontFamily: T.fontBody }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
          <div className="apla-spin" style={{ width: 44, height: 44, border: `3px solid ${T.border}`, borderTopColor: T.amber, borderRadius: "50%" }} />
          <p style={{ color: T.muted, fontSize: 14 }}>Loading engineering plan requests…</p>
        </div>
      </div>
    );
  }

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    // Fragment: modals rendered as siblings to avoid transform stacking-context trapping fixed elements
    <>
    <div style={{ minHeight: "100vh", background: T.bg, fontFamily: T.fontBody }}>
      <div>

        {/* ── Header ──────────────────────────────────────────────────────── */}
        <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 24 }}>
          <div>
            <h1 style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 24, fontWeight: 700, color: "#0c1118", margin: 0, lineHeight: 1.2 }}>
              Engineering Plan Requests
            </h1>
            {!isMobile && (
              <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#6b7280", margin: "4px 0 0" }}>
                Manage purchase requests & company access
              </p>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
            <button
              className="apla-btn"
              onClick={() => { load(); loadOrgAccess(); }}
              disabled={loading}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 12px", background: T.surface, border: `1px solid ${T.border}`, borderRadius: 7, fontSize: 13, color: T.dark, cursor: "pointer", fontFamily: T.fontBody }}
            >
              <IconRefresh /> {!isMobile && "Refresh"}
            </button>
            <button
              type="button"
              className="apla-btn"
              onClick={() => setFiltersOpen(true)}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 14px", background: T.dark, color: "#fff", border: "none", borderRadius: 7, fontSize: 13, cursor: "pointer", fontFamily: T.fontBody }}
            >
              <IconFilter /> {!isMobile && "Filters"}
            </button>
          </div>
        </div>

        {/* ── Tab navigation ───────────────────────────────────────────────── */}
        <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${T.border}`, marginBottom: 28 }}>
          {[
            { id: "requests", label: "Requests" },
            { id: "companies", label: "Company plan access" },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                className="apla-tab-btn"
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: "10px 20px",
                  border: "none",
                  borderBottom: isActive ? `2px solid ${T.dark}` : "2px solid transparent",
                  background: "transparent",
                  color: isActive ? T.dark : T.muted,
                  fontWeight: isActive ? 700 : 400,
                  fontSize: 13,
                  cursor: "pointer",
                  fontFamily: T.fontBody,
                  marginBottom: -2,
                  transition: "color 0.15s",
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* ══════════ TAB: REQUESTS ══════════════════════════════════════════ */}
        {activeTab === "requests" && (
          <>
            {/* KPI Cards */}
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(4, 1fr)", gap: isMobile ? 10 : 16, marginBottom: 24 }}>
              {kpiCards.map((card) => (
                <div
                  key={card.label}
                  className="apla-kpi"
                  style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: isMobile ? "12px 14px" : 20, borderLeft: `4px solid ${card.accent}`, position: "relative", overflow: "hidden" }}
                >
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: isMobile ? 8 : 12 }}>
                    <div style={{ padding: isMobile ? 6 : 8, background: `${card.accent}18`, borderRadius: 7, color: card.accent }}>
                      <card.Icon />
                    </div>
                    <span style={{ fontFamily: T.fontBody, fontSize: isMobile ? 26 : 36, fontWeight: 700, color: T.dark, lineHeight: 1 }}>{card.value}</span>
                  </div>
                  <p style={{ fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: T.dark, margin: "0 0 2px" }}>
                    {card.label}
                  </p>
                  {!isMobile && <p style={{ fontSize: 11, color: T.muted, margin: 0 }}>{card.subtitle}</p>}
                </div>
              ))}
            </div>

            {/* Requests: mobile cards or desktop table */}
            {isMobile ? (
              <div>
                {paginatedRows.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "48px 24px", background: T.surface, borderRadius: 10, border: `1px solid ${T.border}` }}>
                    <IconDoc />
                    <p style={{ color: T.muted, fontSize: 14, marginTop: 10 }}>No engineering plan requests found</p>
                  </div>
                ) : (
                  paginatedRows.map((r) => (
                    <PlanMobileCard
                      key={r.id}
                      r={r}
                      uploadingId={uploadingId}
                      onUpload={(file, el) => complete(r.id, file, el)}
                      onView={() => setViewer({ url: r.fileUrl, title: r.projectTitle || "Plan PDF" })}
                      onRemove={() => removeFile(r.id)}
                      onOpenEditor={() => window.open(r.editorUrl, "_blank", "noopener,noreferrer")}
                      onToggleAccess={() => updateAccess(r.id, { requesterAccessLock: r.requesterAccessLock === "unlocked" ? "locked" : "unlocked" })}
                    />
                  ))
                )}
                {/* Mobile pagination */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginTop: 12 }}>
                  <button
                    type="button"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="apla-btn"
                    style={{ padding: "8px 16px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: page <= 1 ? T.mutedLight : T.dark, cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 13 }}
                  >
                    Previous
                  </button>
                  <span style={{ fontSize: 12, color: T.muted }}>Page {page} of {pageCount}</span>
                  <button
                    type="button"
                    disabled={page >= pageCount}
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    className="apla-btn"
                    style={{ padding: "8px 16px", borderRadius: 7, border: `1px solid ${T.border}`, background: T.surface, color: page >= pageCount ? T.mutedLight : T.dark, cursor: page >= pageCount ? "not-allowed" : "pointer", fontSize: 13 }}
                  >
                    Next
                  </button>
                </div>
              </div>
            ) : (
              <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: T.bg, borderBottom: `1px solid ${T.border}` }}>
                        {["Design", "Requester", "Source", "Status", "Created", "Plan PDF", "Access / Approval", "Actions"].map((h, i) => (
                          <th
                            key={h}
                            style={{
                              padding: "12px 20px",
                              textAlign: i === 7 ? "right" : "left",
                              fontFamily: T.fontBody,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.04em",
                              color: T.muted,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRows.length === 0 ? (
                        <tr>
                          <td colSpan={8} style={{ padding: "48px 24px", textAlign: "center" }}>
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10 }}>
                              <IconDoc />
                              <p style={{ color: T.muted, fontSize: 14 }}>No engineering plan requests found</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        paginatedRows.map((r) => {
                          const isIndividual = r.source === "individual";
                          const isOrg = r.source === "org";
                          const accessLabel =
                            r.requesterAccessLock === "locked"
                              ? "Locked"
                              : r.requesterAccessLock === "unlocked"
                              ? "Unlocked"
                              : "-";
                          const typeLabel =
                            r.type === "REWORK"
                              ? `Rework #${(r.reworkRound ?? 0) || 1}`
                              : "New request";
                          const isUnlocked = r.requesterAccessLock === "unlocked";
                          const badgeStyle = statusBadgeStyle(r.status);
                          const borderColor = rowBorderColor(r.status);

                          return (
                            <tr
                              key={r.id}
                              className="apla-row"
                              style={{ borderLeft: `4px solid ${borderColor}`, borderBottom: `1px solid ${T.border}` }}
                            >
                              {/* Design */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                <div style={{ fontWeight: 600, color: T.dark, marginBottom: 2 }}>{r.projectTitle}</div>
                                <div style={{ fontSize: 11, color: T.muted, fontFamily: T.fontBody }}>{typeLabel}</div>
                              </td>

                              {/* Requester */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                <div style={{ color: T.dark }}>{r.requesterName}</div>
                                <div style={{ fontSize: 11, color: T.mutedLight }}>{r.requesterEmail}</div>
                              </td>

                              {/* Source */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                {isOrg ? (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: T.blueBg, color: T.blue, fontFamily: T.fontBody }}>
                                      Company
                                    </span>
                                    {r.orgName && <span style={{ fontSize: 11, color: T.muted }}>{r.orgName}</span>}
                                  </div>
                                ) : (
                                  <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, background: "rgba(107,114,128,0.1)", color: T.muted, fontFamily: T.fontBody }}>
                                    Individual
                                  </span>
                                )}
                              </td>

                              {/* Status */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 99, fontSize: 11, fontWeight: 600, fontFamily: T.fontBody, ...badgeStyle }}>
                                  {formatStatusLabel(r.status)}
                                </span>
                              </td>

                              {/* Created */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top", fontSize: 12, color: T.muted, whiteSpace: "nowrap" }}>
                                {r.createdAt ? new Date(r.createdAt).toLocaleString() : "-"}
                              </td>

                              {/* Plan PDF */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                {r.fileUrl ? (
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button
                                      type="button"
                                      className="apla-btn"
                                      onClick={() => setViewer({ url: r.fileUrl, title: r.projectTitle || "Plan PDF" })}
                                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11, fontWeight: 600, background: T.dark, color: "#fff", border: "none", borderRadius: 5, cursor: "pointer" }}
                                    >
                                      <FaEye /> View
                                    </button>
                                    <button
                                      type="button"
                                      className="apla-btn"
                                      onClick={() => removeFile(r.id)}
                                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 10px", fontSize: 11, fontWeight: 600, background: T.redBg, color: T.red, border: `1px solid ${T.redBorder}`, borderRadius: 5, cursor: "pointer" }}
                                    >
                                      <FaDeleteLeft /> Delete
                                    </button>
                                  </div>
                                ) : ["PROCESSING", "COMPLETED"].includes(r.status) && !r.fileUrl ? (
                                  <label
                                    style={{
                                      display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 10px", fontSize: 11, fontWeight: 600,
                                      background: uploadingId === r.id ? T.greenBg : "#16a34a",
                                      color: uploadingId === r.id ? T.green : "#fff",
                                      border: uploadingId === r.id ? `1px solid ${T.greenBorder}` : "none",
                                      borderRadius: 5, cursor: uploadingId === r.id ? "not-allowed" : "pointer",
                                    }}
                                  >
                                    {uploadingId === r.id ? (
                                      <>
                                        <div className="apla-spin" style={{ width: 12, height: 12, border: "2px solid #16a34a", borderTopColor: "#fff", borderRadius: "50%" }} />
                                        Uploading…
                                      </>
                                    ) : (
                                      <><IconUpload /> Upload PDF</>
                                    )}
                                    <input type="file" accept="application/pdf" style={{ display: "none" }} disabled={uploadingId === r.id} onChange={(e) => complete(r.id, e.target.files?.[0], e.target)} />
                                  </label>
                                ) : (
                                  <span style={{ color: T.mutedLight, fontSize: 13 }}>—</span>
                                )}
                              </td>

                              {/* Access / Approval */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top" }}>
                                {isIndividual ? (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                      <span style={{ fontSize: 11, color: T.muted }}>
                                        Requester access (<span style={{ fontFamily: T.fontBody, fontSize: 10 }}>{accessLabel === "-" ? "Not configured" : accessLabel}</span>)
                                      </span>
                                      <div
                                        onClick={() => updateAccess(r.id, { requesterAccessLock: isUnlocked ? "locked" : "unlocked" })}
                                        style={{ position: "relative", width: 36, height: 20, borderRadius: 99, background: isUnlocked ? "#22c55e" : T.border, display: "flex", alignItems: "center", padding: "0 2px", cursor: "pointer", transition: "background 0.2s" }}
                                      >
                                        <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,0.2)", transform: isUnlocked ? "translateX(16px)" : "translateX(0)", transition: "transform 0.2s" }} />
                                      </div>
                                    </div>
                                  </div>
                                ) : (
                                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <span style={{ fontSize: 11, color: T.muted }}>Company approval</span>
                                    <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: 99, fontSize: 11, fontWeight: 700, fontFamily: T.fontBody, ...companyApprovalStyle(r.companyApprovalStatus || "pending") }}>
                                      {(r.companyApprovalStatus || "pending").charAt(0).toUpperCase() + (r.companyApprovalStatus || "pending").slice(1)}
                                    </span>
                                  </div>
                                )}
                              </td>

                              {/* Actions */}
                              <td style={{ padding: "14px 20px", verticalAlign: "top", textAlign: "right" }}>
                                {r.editorUrl && (
                                  <button
                                    type="button"
                                    className="apla-btn"
                                    onClick={() => window.open(r.editorUrl, "_blank", "noopener,noreferrer")}
                                    style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "6px 14px", fontSize: 12, fontWeight: 600, background: T.dark, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer" }}
                                  >
                                    <IconExternal /> Open editor
                                  </button>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination footer */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "12px 20px", borderTop: `1px solid ${T.border}`, background: T.bg }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: T.muted }}>
                    <span>Rows per page:</span>
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      className="apla-select"
                      style={{ padding: "4px 8px", border: `1px solid ${T.border}`, borderRadius: 5, fontSize: 12, background: T.surface, color: T.dark }}
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                    </select>
                    <span style={{ marginLeft: 8 }}>
                      Showing {rows.length === 0 ? 0 : startIndex + 1}–{endIndex} of {rows.length}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                    <button
                      type="button"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => Math.max(1, p - 1))}
                      className="apla-btn"
                      style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${T.border}`, background: T.surface, color: page <= 1 ? T.mutedLight : T.dark, cursor: page <= 1 ? "not-allowed" : "pointer", fontSize: 12 }}
                    >
                      Previous
                    </button>
                    <span style={{ color: T.muted }}>Page {page} of {pageCount}</span>
                    <button
                      type="button"
                      disabled={page >= pageCount}
                      onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                      className="apla-btn"
                      style={{ padding: "5px 12px", borderRadius: 5, border: `1px solid ${T.border}`, background: T.surface, color: page >= pageCount ? T.mutedLight : T.dark, cursor: page >= pageCount ? "not-allowed" : "pointer", fontSize: 12 }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {/* ══════════ TAB: COMPANY ACCESS ════════════════════════════════════ */}
        {activeTab === "companies" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10, padding: 20 }}>
              {/* Header row */}
              <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 }}>
                <div>
                  <h2 style={{ fontFamily: T.fontBody, fontSize: 20, color: T.dark, margin: 0, letterSpacing: "0.03em" }}>Company plan access</h2>
                  <p style={{ fontSize: 12, color: T.muted, margin: "2px 0 0" }}>Lock or unlock engineered plan access per company.</p>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input
                    type="text"
                    value={orgSearch}
                    onChange={(e) => setOrgSearch(e.target.value)}
                    placeholder="Search company…"
                    className="apla-input"
                    style={{ padding: "7px 12px", border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13, fontFamily: T.fontBody, background: T.surface, color: T.dark }}
                  />
                  {loadingOrgAccess && <span style={{ fontSize: 11, color: T.mutedLight }}>Loading…</span>}
                </div>
              </div>

              {filteredOrgRows.length === 0 ? (
                <p style={{ fontSize: 13, color: T.muted }}>No companies found. Try changing the search term.</p>
              ) : isMobile ? (
                <div>
                  {filteredOrgRows.map((o) => (
                    <OrgMobileCard key={o.orgId} o={o} updatingOrgId={updatingOrgId} onToggle={() => toggleOrgAccess(o)} />
                  ))}
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${T.border}` }}>
                        {["Company", "Created", "Access", "Requests", "Action"].map((h, i) => (
                          <th
                            key={h}
                            style={{ padding: "8px 12px", textAlign: i === 4 ? "right" : "left", fontFamily: T.fontBody, fontSize: 10, fontWeight: 700, letterSpacing: "0.04em", color: T.muted }}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrgRows.map((o) => (
                        <tr key={o.orgId} className="apla-org-row" style={{ borderBottom: `1px solid ${T.border}` }}>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{ fontWeight: 600, color: T.dark }}>{o.orgName}</span>
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: T.muted }}>
                            {o.createdAt ? new Date(o.createdAt).toLocaleDateString() : "-"}
                          </td>
                          <td style={{ padding: "10px 12px" }}>
                            <span style={{
                              display: "inline-block", padding: "2px 9px", borderRadius: 99,
                              fontSize: 11, fontWeight: 700, fontFamily: T.fontBody,
                              background: o.accessLock === "locked" ? T.redBg : T.greenBg,
                              color: o.accessLock === "locked" ? T.red : T.green,
                            }}>
                              {o.accessLock === "locked" ? "Locked" : "Unlocked"}
                            </span>
                          </td>
                          <td style={{ padding: "10px 12px", fontSize: 12, color: T.muted }}>
                            {o.stats?.total ?? 0} total • {o.stats?.completed ?? 0} completed
                          </td>
                          <td style={{ padding: "10px 12px", textAlign: "right" }}>
                            <button
                              type="button"
                              className="apla-toggle-btn"
                              onClick={() => toggleOrgAccess(o)}
                              disabled={updatingOrgId === o.orgId}
                              style={{ display: "inline-flex", alignItems: "center", padding: "5px 14px", fontSize: 12, fontWeight: 600, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, cursor: updatingOrgId === o.orgId ? "not-allowed" : "pointer", color: T.dark, opacity: updatingOrgId === o.orgId ? 0.5 : 1 }}
                            >
                              {updatingOrgId === o.orgId ? "Updating…" : o.accessLock === "locked" ? "Unlock" : "Lock"}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>

    {/* ── PDF Viewer Modal — outside main div, no transform-parent issue ── */}
    {viewer && (
      <div
        onClick={() => setViewer(null)}
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ background: "#0f172a", color: "#f8fafc", width: isMobile ? "96%" : "70%", height: isMobile ? "85%" : "80%", borderRadius: 10, boxShadow: "0 24px 60px rgba(0,0,0,0.5)", display: "flex", flexDirection: "column", overflow: "hidden" }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 20px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <div>
              <p style={{ fontWeight: 700, fontSize: 14, color: "#fff", margin: 0 }}>{viewer.title}</p>
              <p style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", margin: "2px 0 0" }}>Engineering plan (PDF viewer)</p>
            </div>
            <button
              type="button"
              onClick={() => setViewer(null)}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", border: "none", background: "transparent", color: "rgba(255,255,255,0.7)", cursor: "pointer" }}
            >
              <IconX />
            </button>
          </div>
          <div style={{ flex: 1, background: "#111827" }}>
            <iframe src={viewer.url} title="Engineering plan PDF" style={{ width: "100%", height: "100%", border: "none" }} />
          </div>
        </div>
      </div>
    )}

    {/* ── Filter popup dialog — centered modal, outside main div ────────── */}
    {filtersOpen && (
      <div
        onClick={() => setFiltersOpen(false)}
        style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{ background: T.surface, borderRadius: 12, width: "100%", maxWidth: 500, maxHeight: "90vh", margin: "0 16px", display: "flex", flexDirection: "column", boxShadow: "0 24px 60px rgba(0,0,0,0.25)", fontFamily: T.fontBody }}
        >
          {/* Dialog header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: `1px solid ${T.border}` }}>
            <div>
              <p style={{ fontWeight: 700, fontSize: 15, color: T.dark, margin: 0 }}>Filters</p>
              <p style={{ fontSize: 12, color: T.muted, margin: "2px 0 0" }}>Narrow down plan requests.</p>
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen(false)}
              style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 32, height: 32, borderRadius: "50%", border: "none", background: "transparent", cursor: "pointer", color: T.dark, fontSize: 18 }}
            >
              <IconX />
            </button>
          </div>

          {/* Dialog body */}
          <div style={{ flex: 1, overflowY: "auto", padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <FilterLabel>Design title</FilterLabel>
              <FInput value={titleFilter} onChange={(e) => setTitleFilter(e.target.value)} placeholder="Search by project name…" />
            </div>
            <div>
              <FilterLabel>Requester</FilterLabel>
              <FInput value={requesterFilter} onChange={(e) => setRequesterFilter(e.target.value)} placeholder="Name or email…" />
            </div>
            <div>
              <FilterLabel>Status</FilterLabel>
              <FSelect value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All statuses</option>
                <option value="PENDING_PAYMENT">Pending payment</option>
                <option value="PROCESSING">Processing</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELED">Canceled</option>
              </FSelect>
            </div>
            <div>
              <FilterLabel>Source</FilterLabel>
              <FSelect value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
                <option value="all">All sources</option>
                <option value="org">Company requests</option>
                <option value="individual">Individual users</option>
              </FSelect>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <FilterLabel>From (created)</FilterLabel>
                <FInput type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
              </div>
              <div>
                <FilterLabel>To (created)</FilterLabel>
                <FInput type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
              </div>
            </div>
          </div>

          {/* Dialog footer */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 24px", borderTop: `1px solid ${T.border}`, background: T.bg, borderRadius: "0 0 12px 12px" }}>
            <button
              type="button"
              onClick={() => {
                setStatusFilter("all");
                setSourceFilter("all");
                setTitleFilter("");
                setRequesterFilter("");
                setDateFrom("");
                setDateTo("");
                load();
              }}
              style={{ padding: "8px 18px", fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 6, background: T.surface, color: T.muted, cursor: "pointer" }}
            >
              Reset
            </button>
            <button
              type="button"
              onClick={() => { load(); setFiltersOpen(false); }}
              style={{ padding: "8px 24px", fontSize: 13, fontWeight: 700, border: "none", borderRadius: 6, background: T.dark, color: "#fff", cursor: "pointer" }}
            >
              Apply filters
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
